/**
 * @author Fernando Ruano
 * @file index.js
 * @description Authentication Services
 */

// Core
import endpoints from '@nephro/config/endpoints';
import { externalConnector } from '@nephro/core/connector';

// Endpoints
const { authentication: authenticationEndpoints } = endpoints;

export class AuthenticationService {
  constructor(http) {
    this.http = http;
  }

  /**
   * Authenticates user with email and password
   * @param { string } userName - The username.
   * @param { string } credentials - The user password.
   * @param { string } identityProvider - The identity provider (staff or patient).
   * @param { string } isEmail - If userName is email or ID (staff or patient).
   * @param { string } documentType - Patient identification type
   * @return { Object } - Authenticated user
   */
  login = async (
    userName,
    credentials,
    identityProvider,
    isEmail,
    documentType
  ) => {
    const body = {
      userName,
      credentials,
      identityProvider,
      isEmail,
      documentType
    };
    const url = authenticationEndpoints.login;
    return this.http.post(url, body);
  };

  /**
   * Reset password by email.
   * @param { string } email - The user email.
   * @param { string } userNewPassword - The new user password.
   * @return { promise } - The promise object.
   */
  resetPassword = async (email, userNewPassword) => {
    const newPasswordEncoded = encodeURIComponent(userNewPassword);
    const queryParam = `?userName=${email}&userNewPassword=${newPasswordEncoded}`;
    let url = authenticationEndpoints.resetPassword;
    url = `${url}${queryParam}`;
    return this.http.get(url);
  };

  /**
   * Reset password by email with username encrypted
   * @param { string } identificationDocument - Identification Document Id.
   * @param { string } documentType - Document Type Id.
   * @param { string } email - The user email.
   * @param { string } userNewPassword - The new user password.
   * @return { promise } - The promise object.
   */
  passwordResetRegister = async (
    identificationDocument,
    documentType,
    email,
    userNewPassword
  ) => {
    const newPasswordEncoded = encodeURIComponent(userNewPassword);
    const queryParam = `?identificationDocument=${identificationDocument}&documentType=${documentType}&userName=${email}&userNewPassword=${newPasswordEncoded}`;

    let url = authenticationEndpoints.resetPasswordRegister;
    url = `${url}${queryParam}`;
    return this.http.get(url);
  };

  /**
   * Reset password by email with username encrypted
   * @param { string } identificationDocument - Identification Document Id.
   * @param { string } documentType - Document Type Id.
   * @param { string } email - The user email.
   * @return { promise } - The promise object.
   */
  activatePatientByRegister = async (
    identificationDocument,
    documentType,
    email
  ) => {
    const params = {
      identificationDocument,
      documentType,
      userName: email
    };

    let url = authenticationEndpoints.activatePatientByRegister;
    url = `${url}`;
    return this.http.get(url, { params });
  };

  /**
   * signOut.
   * @param { string } identityProvider - The identityProvider.
   * @param { string } userName - The userName.
   * @return { promise } - The promise object.
   */
  signOut = async (identityProvider, userName) => {
    const body = {
      identityProvider,
      userName
    };
    const url = authenticationEndpoints.signOut;
    return this.http.post(url, body);
  };

  /**
   * Recover password by email.
   * @param { string } email - The user email.
   * @return { promise } - The promise object.
   */
  recoverPassword = async (email) => {
    const body = {
      isEmail: '1',
      userName: email
    };
    const url = authenticationEndpoints.recoverPassword;
    return this.http.post(url, body);
  };

  /**
   * Recover password by token.
   * @param { string } token - The user token.
   * @return { promise } - The promise object.
   */
  getClaims = async (token) => {
    const queryParam = `?token=${token}`;
    let url = authenticationEndpoints.getClaims;
    url = `${url}${queryParam}`;
    return this.http.post(url);
  };

  /**
   * Get user list by user name filter.
   * @param { string } identityProvider - The identity provider (staff or patient).
   * @param { string } filter - Filter.
   * @return { promise } - The promise object.
   */
  getUserList = async (identityProvider, filter) => {
    const queryParam = `?identityProvider=${identityProvider}&userFilterName=${filter}`;
    let url = authenticationEndpoints.userList;
    url = `${url}${queryParam}`;
    return this.http.get(url);
  };

  /**
   * Send OTP Code
   * @param { string } stateToken - State Token.
   * @param { string } factorId - Factor Identification.
   * @param { string } identityProvider - The identity provider (staff or patient).
   * @return { Object } - A State Token to continue with the next step
   */
  sendCode = async (stateToken, factorId, identityProvider) => {
    const queryParam = `?stateToken=${stateToken}&factorId=${factorId}&identityProvider=${identityProvider}`;
    let url = authenticationEndpoints.factor.challenge;
    url = `${url}${queryParam}`;
    return this.http.post(url);
  };

  /**
   * Send OTP Code
   * @param { string } passcode - Passcode.
   * @param { string } stateToken - State Token.
   * @param { string } factorId - Factor Identification.
   * @param { string } identityProvider - The identity provider (staff or patient).
   * @return { Object } - A State Token to continue with the next step
   */
  verifyCode = async (
    username,
    passcode,
    stateToken,
    factorId,
    identityProvider
  ) => {
    const queryParam = `?username=${username}&passcode=${passcode}&stateToken=${stateToken}&factorId=${factorId}&identityProvider=${identityProvider}`;
    let url = authenticationEndpoints.factor.verify;
    url = `${url}${queryParam}`;
    return this.http.post(url);
  };

  /**
   * Get Access Token
   * @param { string } sessionToken - Session Token.
   * @param { string } identityProvider - The identity provider (staff or patient).
   * @return { Object } - A Access Token
   */
  getToken = async (sessionToken, identityProvider) => {
    const queryParam = `?sessionToken=${sessionToken}&identityProvider=${identityProvider}`;
    let url = authenticationEndpoints.requestToken;
    url = `${url}${queryParam}`;
    return this.http.post(url);
  };

  /**
   * Auto Enroll Factors
   * @param { string } username - Username.
   * @param { string } accessToken - Access Token.
   * @param { string } identityProvider - The identity provider (staff or patient).
   * @return { Object } - A Access Token
   */
  autoEnrollFactor = async (username, accessToken, identityProvider) => {
    const queryParam = `?username=${username}&accessToken=${accessToken}&identityProvider=${identityProvider}`;
    let url = authenticationEndpoints.autoEnrollFactor;
    url = `${url}${queryParam}`;
    return this.http.post(url);
  };
}

export default new AuthenticationService(externalConnector);
